// ForgotPasswordScreen.js — Full-featured password recovery with real API integration

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, TextInput, TouchableOpacity, Alert,
  KeyboardAvoidingView, Platform, ScrollView, ActivityIndicator,
  Animated, Vibration, Dimensions, Clipboard,
 Linking, 
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';

const { width } = Dimensions.get('window');

const API_BASE  = 'https://api.jobeng.ai';
const API_KEY   = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const RESEND_COOLDOWN_SECS = 60;
const TOKEN_EXPIRY_MINS    = 24 * 60; // 24h in minutes

// ── API helpers ──────────────────────────────────────────────────────────────
const postJSON = async (path, body) => {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw { status: res.status, ...data };
  return data;
};

// ── Password strength analyser ───────────────────────────────────────────────
const analysePassword = (pw) => {
  const checks = {
    length:    pw.length >= 8,
    uppercase: /[A-Z]/.test(pw),
    lowercase: /[a-z]/.test(pw),
    number:    /[0-9]/.test(pw),
    special:   /[^A-Za-z0-9]/.test(pw),
    long:      pw.length >= 12,
  };
  const score = Object.values(checks).filter(Boolean).length;
  const strength =
    score <= 2 ? { label: 'Very Weak', color: '#D32F2F', bars: 1 } :
    score === 3 ? { label: 'Weak',     color: '#F57C00', bars: 2 } :
    score === 4 ? { label: 'Fair',     color: '#F9A825', bars: 3 } :
    score === 5 ? { label: 'Strong',   color: '#388E3C', bars: 4 } :
                  { label: 'Very Strong', color: '#1B5E20', bars: 5 };
  return { checks, score, ...strength };
};

// ── Subcomponents ─────────────────────────────────────────────────────────────
const StrengthBar = ({ pw }) => {
  if (!pw) return null;
  const { label, color, bars, checks } = analysePassword(pw);
  return (
    <View style={{ marginTop: 10 }}>
      <View style={{ flexDirection: 'row', gap: 4, marginBottom: 6 }}>
        {[1,2,3,4,5].map(i => (
          <View key={i} style={{ flex: 1, height: 4, borderRadius: 2,
            backgroundColor: i <= bars ? color : '#E0E0E0' }} />
        ))}
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text style={{ fontSize: 12, color, fontWeight: '700' }}>{label}</Text>
        <Text style={{ fontSize: 11, color: '#9E9E9E' }}>{pw.length} chars</Text>
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
        {[
          { key: 'length',    label: '8+ chars'    },
          { key: 'uppercase', label: 'Uppercase'   },
          { key: 'lowercase', label: 'Lowercase'   },
          { key: 'number',    label: 'Number'      },
          { key: 'special',   label: 'Symbol'      },
          { key: 'long',      label: '12+ chars'   },
        ].map(c => (
          <View key={c.key} style={[S.strengthTag,
            { backgroundColor: checks[c.key] ? '#E8F5E9' : '#F5F5F5',
              borderColor: checks[c.key] ? '#A5D6A7' : '#E0E0E0' }]}>
            <Ionicons name={checks[c.key] ? 'checkmark' : 'close'}
              size={11} color={checks[c.key] ? '#388E3C' : '#BDBDBD'} />
            <Text style={{ fontSize: 11, marginLeft: 3,
              color: checks[c.key] ? '#388E3C' : '#9E9E9E' }}>{c.label}</Text>
          </View>
        ))}
      </View>
    </View>
  );
};

const InputField = ({
  label, value, onChange, placeholder, icon, secure, error,
  keyboard = 'default', autoCapitalize = 'none', autoComplete, info,
  onToggleSecure, isSecureVisible, maxLength, autoFocus,
}) => {
  const anim = useRef(new Animated.Value(error ? 1 : 0)).current;
  useEffect(() => {
    Animated.spring(anim, { toValue: error ? 1 : 0, useNativeDriver: false }).start();
  }, [error]);
  const borderColor = anim.interpolate({ inputRange: [0,1], outputRange: ['#E0E0E0','#D32F2F'] });
  return (
    <View style={{ marginBottom: 18 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
        <Text style={S.label}>{label}</Text>
        {info && <Text style={{ fontSize: 11, color: '#9E9E9E' }}>{info}</Text>}
      </View>
      <Animated.View style={[S.inputBox, { borderColor }]}>
        <MaterialCommunityIcons name={icon} size={20} color="#90A4AE" style={{ marginRight: 10 }} />
        <TextInput
          style={S.input}
          value={value}
          onChangeText={onChange}
          placeholder={placeholder}
          placeholderTextColor="#BDBDBD"
          keyboardType={keyboard}
          autoCapitalize={autoCapitalize}
          autoCorrect={false}
          autoComplete={autoComplete}
          secureTextEntry={secure && !isSecureVisible}
          maxLength={maxLength}
          autoFocus={autoFocus}
          returnKeyType="done"
        />
        {secure && (
          <TouchableOpacity onPress={onToggleSecure} style={{ padding: 4 }}>
            <MaterialCommunityIcons name={isSecureVisible ? 'eye-off' : 'eye'}
              size={20} color="#90A4AE" />
          </TouchableOpacity>
        )}
        {!secure && value?.length > 0 && (
          <TouchableOpacity onPress={() => onChange('')} style={{ padding: 4 }}>
            <MaterialCommunityIcons name="close-circle" size={18} color="#B0BEC5" />
          </TouchableOpacity>
        )}
      </Animated.View>
      {error ? (
        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 5, gap: 5 }}>
          <MaterialCommunityIcons name="alert-circle" size={14} color="#D32F2F" />
          <Text style={S.errText}>{error}</Text>
        </View>
      ) : null}
    </View>
  );
};

const StepDot = ({ step, active, done }) => (
  <View style={{ alignItems: 'center' }}>
    <View style={[S.stepDot,
      done  && { backgroundColor: '#388E3C', borderColor: '#388E3C' },
      active && !done && { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
    ]}>
      {done
        ? <Ionicons name="checkmark" size={14} color="#fff" />
        : <Text style={{ fontSize: 12, fontWeight: '800',
            color: active ? '#fff' : '#B0BEC5' }}>{step}</Text>}
    </View>
  </View>
);

const StepLine = ({ done }) => (
  <View style={{ flex: 1, height: 2, marginTop: -11,
    backgroundColor: done ? '#388E3C' : '#E0E0E0', marginHorizontal: 4 }} />
);

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function ForgotPasswordScreen({ navigation, route }) {
  // ── Steps: 0=email, 1=sent, 2=reset, 3=done
  const [step,        setStep]        = useState(0);

  // Step 0 — email
  const [email,       setEmail]       = useState('');
  const [emailErr,    setEmailErr]    = useState('');
  const [sending,     setSending]     = useState(false);

  // Step 1 — sent / token
  const [cooldown,    setCooldown]    = useState(0);
  const [resending,   setResending]   = useState(false);
  const [sentAt,      setSentAt]      = useState(null);

  // Step 2 — reset
  const [token,       setToken]       = useState('');
  const [tokenErr,    setTokenErr]    = useState('');
  const [newPw,       setNewPw]       = useState('');
  const [newPwErr,    setNewPwErr]    = useState('');
  const [confirmPw,   setConfirmPw]   = useState('');
  const [confirmErr,  setConfirmErr]  = useState('');
  const [showNewPw,   setShowNewPw]   = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [resetting,   setResetting]   = useState(false);

  // Which portal opened this screen? Passed as route param from the login
  // screen (business / professional / learning). Defaults to professional.
  const userType = String(route?.params?.userType || route?.params?.portal || 'professional').toLowerCase();
  const loginRoute =
    userType === 'business' ? 'BusinessLogin'
    : userType === 'learning' ? 'LearningLoginScreen'
    : 'ProfessionalLogin';
  const goToLogin = useCallback(() => {
    // reset the stack so back-button doesn't return into the reset flow
    if (navigation.reset) {
      navigation.reset({ index: 0, routes: [{ name: loginRoute }] });
    } else {
      navigation.navigate(loginRoute);
    }
  }, [navigation, loginRoute]);

  // Deep-link token pre-fill (e.g. from email link)
  useEffect(() => {
    const prefill = route?.params?.token;
    if (prefill) { setToken(prefill); setStep(2); }
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown(c => c <= 1 ? (clearInterval(t), 0) : c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  // Fade animation for step transitions
  const fade = useRef(new Animated.Value(1)).current;
  const transition = (fn) => {
    Animated.sequence([
      Animated.timing(fade, { toValue: 0, duration: 160, useNativeDriver: true }),
    ]).start(() => { fn(); Animated.timing(fade, { toValue: 1, duration: 220, useNativeDriver: true }).start(); });
  };

  // ── Validation ───────────────────────────────────────────────────────────────
  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const validatePasswordReset = () => {
    let ok = true;
    if (!token.trim()) { setTokenErr('Reset code is required'); ok = false; }
    else setTokenErr('');
    if (!newPw) { setNewPwErr('New password is required'); ok = false; }
    else if (newPw.length < 8) { setNewPwErr('Password must be at least 8 characters'); ok = false; }
    else if (analysePassword(newPw).score < 3) { setNewPwErr('Password is too weak. Add uppercase, numbers, or symbols.'); ok = false; }
    else setNewPwErr('');
    if (!confirmPw) { setConfirmErr('Please confirm your password'); ok = false; }
    else if (confirmPw !== newPw) { setConfirmErr('Passwords do not match'); ok = false; }
    else setConfirmErr('');
    return ok;
  };

  // ── Step 0: Request reset ─────────────────────────────────────────────────────
  const requestReset = async (isResend = false) => {
    if (!isResend) {
      setEmailErr('');
      if (!email.trim()) { setEmailErr('Email address is required'); return; }
      if (!validEmail(email)) { setEmailErr('Please enter a valid email address'); return; }
    }

    isResend ? setResending(true) : setSending(true);

    try {
      await postJSON('/api/auth/forgot-password', {
        email: email.toLowerCase().trim(),
      });
      setSentAt(new Date());
      setCooldown(RESEND_COOLDOWN_SECS);
      if (!isResend) transition(() => setStep(1));
      else Alert.alert('Email Resent', `A new reset link has been sent to ${email}.`);
    } catch (err) {
      const msg = err?.message || err?.error || 'Could not send reset email. Please try again.';
      // Always show generic message for security (don't confirm if account exists)
      if (!isResend) {
        // Still advance to sent screen to avoid email enumeration
        setSentAt(new Date());
        setCooldown(RESEND_COOLDOWN_SECS);
        transition(() => setStep(1));
      } else {
        Alert.alert('Error', msg);
      }
    } finally {
      isResend ? setResending(false) : setSending(false);
    }
  };

  // ── Step 2: Submit new password ──────────────────────────────────────────────
  const submitReset = async () => {
    if (!validatePasswordReset()) { Vibration.vibrate(80); return; }
    setResetting(true);
    try {
      await postJSON('/api/auth/reset-password', {
        token: token.trim(),
        new_password: newPw,
      });
      transition(() => setStep(3));
    } catch (err) {
      const msg = err?.message || err?.error || '';
      if (err?.status === 400 || msg.toLowerCase().includes('token') || msg.toLowerCase().includes('expired')) {
        setTokenErr('This reset code is invalid or has expired. Please request a new one.');
      } else if (err?.status === 422) {
        setNewPwErr(msg || 'Password does not meet requirements.');
      } else {
        Alert.alert('Reset Failed', msg || 'Unable to reset password. Please try again or request a new link.');
      }
    } finally {
      setResetting(false);
    }
  };

  // ── Computed ─────────────────────────────────────────────────────────────────
  const expiryText = () => {
    if (!sentAt) return null;
    const mins = Math.floor((Date.now() - sentAt) / 60000);
    const remaining = TOKEN_EXPIRY_MINS - mins;
    if (remaining <= 0) return 'Link has expired — please request a new one.';
    if (remaining < 60) return `Link expires in ${remaining} minute${remaining !== 1 ? 's' : ''}.`;
    const hrs = Math.floor(remaining / 60);
    return `Link expires in ${hrs} hour${hrs !== 1 ? 's' : ''}.`;
  };

  // ── Renders ──────────────────────────────────────────────────────────────────
  const renderStep0 = () => (
    <>
      <View style={S.iconWrap}>
        <LinearGradient colors={['#E3F2FD','#BBDEFB']} style={S.iconCircle}>
          <MaterialCommunityIcons name="lock-reset" size={52} color="#0e4e96" />
        </LinearGradient>
      </View>
      <Text style={S.heading}>Forgot your password?</Text>
      <Text style={S.sub}>
        Enter the email address linked to your account and we'll send you a secure reset link.
      </Text>

      <InputField
        label="Email Address"
        value={email}
        onChange={v => { setEmail(v); setEmailErr(''); }}
        placeholder="your@email.com"
        icon="email-outline"
        keyboard="email-address"
        autoComplete="email"
        error={emailErr}
        autoFocus
      />

      <TouchableOpacity
        style={[S.primaryBtn, sending && S.primaryBtnDisabled]}
        onPress={() => requestReset(false)}
        disabled={sending}
        activeOpacity={0.8}>
        {sending
          ? <ActivityIndicator color="#fff" size={20} />
          : <>
              <MaterialCommunityIcons name="email-fast-outline" size={20} color="#fff" style={{ marginRight: 8 }} />
              <Text style={S.primaryBtnText}>Send Reset Link</Text>
            </>}
      </TouchableOpacity>

      <View style={S.securityBox}>
        <MaterialCommunityIcons name="shield-lock-outline" size={18} color="#388E3C" />
        <Text style={S.securityText}>
          The reset link is valid for 24 hours and can only be used once. We never share your email.
        </Text>
      </View>
    </>
  );

  const renderStep1 = () => (
    <>
      <View style={S.iconWrap}>
        <LinearGradient colors={['#E8F5E9','#C8E6C9']} style={S.iconCircle}>
          <MaterialCommunityIcons name="email-check-outline" size={52} color="#388E3C" />
        </LinearGradient>
      </View>
      <Text style={S.heading}>Check your inbox</Text>
      <Text style={S.sub}>We've sent a reset link to:</Text>
      <View style={S.emailPill}>
        <MaterialCommunityIcons name="email" size={16} color="#0e4e96" style={{ marginRight: 7 }} />
        <Text style={S.emailPillText} numberOfLines={1}>{email}</Text>
      </View>

      <View style={S.stepsGuide}>
        {[
          { icon: 'email-open-outline', text: 'Open the email from JobEng' },
          { icon: 'cursor-default-click-outline', text: 'Click the "Reset Password" button' },
          { icon: 'lock-reset', text: 'Enter your new password on the next screen' },
        ].map((s, i) => (
          <View key={i} style={S.stepGuideRow}>
            <View style={S.stepGuideNum}><Text style={{ color: '#0e4e96', fontSize: 12, fontWeight: '800' }}>{i + 1}</Text></View>
            <MaterialCommunityIcons name={s.icon} size={18} color="#0e4e96" style={{ marginHorizontal: 10 }} />
            <Text style={{ flex: 1, fontSize: 14, color: '#546E7A', lineHeight: 20 }}>{s.text}</Text>
          </View>
        ))}
      </View>

      {expiryText() && (
        <View style={[S.securityBox, { backgroundColor: '#FFF3E0', borderColor: '#FFCC80' }]}>
          <MaterialCommunityIcons name="clock-outline" size={16} color="#F57C00" />
          <Text style={[S.securityText, { color: '#E65100' }]}>{expiryText()}</Text>
        </View>
      )}

      <View style={{ flexDirection: 'row', gap: 10, marginBottom: 10 }}>
        {/* I have the code — skip to reset */}
        <TouchableOpacity style={[S.primaryBtn, { flex: 1 }]}
          onPress={() => transition(() => setStep(2))} activeOpacity={0.8}>
          <MaterialCommunityIcons name="form-textbox-password" size={18} color="#fff" style={{ marginRight: 6 }} />
          <Text style={S.primaryBtnText}>Enter Code</Text>
        </TouchableOpacity>

        {/* Resend */}
        <TouchableOpacity
          style={[S.outlineBtn, { flex: 1 }, (cooldown > 0 || resending) && { opacity: 0.5 }]}
          onPress={() => requestReset(true)}
          disabled={cooldown > 0 || resending}
          activeOpacity={0.8}>
          {resending
            ? <ActivityIndicator color="#0e4e96" size={16} />
            : <>
                <MaterialCommunityIcons name="refresh" size={18} color="#0e4e96" style={{ marginRight: 6 }} />
                <Text style={S.outlineBtnText}>{cooldown > 0 ? `Resend (${cooldown}s)` : 'Resend'}</Text>
              </>}
        </TouchableOpacity>
      </View>

      <View style={S.spamNote}>
        <MaterialCommunityIcons name="information-outline" size={14} color="#78909C" />
        <Text style={S.spamText}>
          Can't find it? Check your spam or junk folder. It may take a minute to arrive.
        </Text>
      </View>

      {/* Wrong email? */}
      <TouchableOpacity style={{ alignItems: 'center', paddingVertical: 10 }}
        onPress={() => transition(() => { setStep(0); setEmail(''); })}>
        <Text style={{ fontSize: 13, color: '#0e4e96', fontWeight: '600' }}>
          Wrong email? Go back to change it
        </Text>
      </TouchableOpacity>
    </>
  );

  const renderStep2 = () => {
    const strength = newPw ? analysePassword(newPw) : null;
    const pwMatch  = confirmPw.length > 0 && confirmPw === newPw;
    return (
      <>
        <View style={S.iconWrap}>
          <LinearGradient colors={['#EDE7F6','#D1C4E9']} style={S.iconCircle}>
            <MaterialCommunityIcons name="form-textbox-password" size={52} color="#5E35B1" />
          </LinearGradient>
        </View>
        <Text style={S.heading}>Set new password</Text>
        <Text style={S.sub}>
          Enter the 6-digit code from your email and choose a strong new password.
        </Text>

        {/* Token field */}
        <View style={{ marginBottom: 18 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
            <Text style={S.label}>Reset Code</Text>
            <TouchableOpacity onPress={async () => {
              const clip = await Clipboard.getString?.();
              if (clip?.trim()) setToken(clip.trim());
            }}>
              <Text style={{ fontSize: 12, color: '#0e4e96', fontWeight: '600' }}>Paste</Text>
            </TouchableOpacity>
          </View>
          <Animated.View style={[S.inputBox, tokenErr && { borderColor: '#D32F2F' }]}>
            <MaterialCommunityIcons name="key-variant" size={20} color="#90A4AE" style={{ marginRight: 10 }} />
            <TextInput
              style={[S.input, { letterSpacing: 4, fontWeight: '700' }]}
              value={token}
              onChangeText={v => { setToken(v.replace(/\s/g, '')); setTokenErr(''); }}
              placeholder="e.g. ABC123XYZ"
              placeholderTextColor="#BDBDBD"
              autoCapitalize="characters"
              autoCorrect={false}
              maxLength={64}
            />
            {token.length > 0 && (
              <TouchableOpacity onPress={() => setToken('')} style={{ padding: 4 }}>
                <MaterialCommunityIcons name="close-circle" size={18} color="#B0BEC5" />
              </TouchableOpacity>
            )}
          </Animated.View>
          {tokenErr ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 5, gap: 5 }}>
              <MaterialCommunityIcons name="alert-circle" size={14} color="#D32F2F" />
              <Text style={S.errText}>{tokenErr}</Text>
            </View>
          ) : null}
        </View>

        {/* New password */}
        <InputField
          label="New Password"
          value={newPw}
          onChange={v => { setNewPw(v); setNewPwErr(''); }}
          placeholder="Minimum 8 characters"
          icon="lock-outline"
          secure
          isSecureVisible={showNewPw}
          onToggleSecure={() => setShowNewPw(v => !v)}
          error={newPwErr}
          info={strength ? `${strength.label}` : ''}
        />
        <StrengthBar pw={newPw} />

        {/* Confirm password */}
        <View style={{ marginTop: 16 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
            <Text style={S.label}>Confirm Password</Text>
            {pwMatch && <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
              <Ionicons name="checkmark-circle" size={14} color="#388E3C" />
              <Text style={{ fontSize: 12, color: '#388E3C', fontWeight: '600' }}>Passwords match</Text>
            </View>}
          </View>
          <Animated.View style={[S.inputBox, confirmErr && { borderColor: '#D32F2F' },
            pwMatch && { borderColor: '#66BB6A' }]}>
            <MaterialCommunityIcons name="lock-check-outline" size={20}
              color={pwMatch ? '#66BB6A' : '#90A4AE'} style={{ marginRight: 10 }} />
            <TextInput
              style={S.input}
              value={confirmPw}
              onChangeText={v => { setConfirmPw(v); setConfirmErr(''); }}
              placeholder="Repeat your new password"
              placeholderTextColor="#BDBDBD"
              autoCapitalize="none"
              autoCorrect={false}
              secureTextEntry={!showConfirm}
            />
            <TouchableOpacity onPress={() => setShowConfirm(v => !v)} style={{ padding: 4 }}>
              <MaterialCommunityIcons name={showConfirm ? 'eye-off' : 'eye'}
                size={20} color="#90A4AE" />
            </TouchableOpacity>
          </Animated.View>
          {confirmErr ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 5, gap: 5 }}>
              <MaterialCommunityIcons name="alert-circle" size={14} color="#D32F2F" />
              <Text style={S.errText}>{confirmErr}</Text>
            </View>
          ) : null}
        </View>

        <TouchableOpacity
          style={[S.primaryBtn, { marginTop: 24 }, (resetting || !token || !newPw || !confirmPw) && S.primaryBtnDisabled]}
          onPress={submitReset}
          disabled={resetting || !token || !newPw || !confirmPw}
          activeOpacity={0.8}>
          {resetting
            ? <ActivityIndicator color="#fff" size={20} />
            : <>
                <MaterialCommunityIcons name="lock-check" size={20} color="#fff" style={{ marginRight: 8 }} />
                <Text style={S.primaryBtnText}>Reset Password</Text>
              </>}
        </TouchableOpacity>

        {/* Expired / go back */}
        <TouchableOpacity style={{ alignItems: 'center', paddingVertical: 14 }}
          onPress={() => transition(() => { setStep(1); setToken(''); setNewPw(''); setConfirmPw(''); })}>
          <Text style={{ fontSize: 13, color: '#78909C' }}>
            <Text style={{ color: '#0e4e96', fontWeight: '600' }}>Code not working?</Text>
            {'  '}Request a new one
          </Text>
        </TouchableOpacity>
      </>
    );
  };

  const renderStep3 = () => (
    <>
      <View style={S.iconWrap}>
        <LinearGradient colors={['#E8F5E9','#A5D6A7']} style={S.iconCircle}>
          <MaterialCommunityIcons name="check-circle-outline" size={60} color="#1B5E20" />
        </LinearGradient>
      </View>
      <Text style={[S.heading, { color: '#1B5E20' }]}>Password Reset!</Text>
      <Text style={S.sub}>
        Your password has been changed successfully. You can now sign in with your new password.
      </Text>

      <View style={S.successList}>
        {[
          'Your old password is now invalid',
          'All other active sessions have been logged out',
          'A confirmation email has been sent to you',
        ].map((item, i) => (
          <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
            <Ionicons name="checkmark-circle" size={18} color="#388E3C" style={{ marginTop: 1 }} />
            <Text style={{ flex: 1, fontSize: 14, color: '#546E7A', lineHeight: 20 }}>{item}</Text>
          </View>
        ))}
      </View>

      <TouchableOpacity style={[S.primaryBtn, { backgroundColor: '#388E3C' }]}
        onPress={goToLogin} activeOpacity={0.8}>
        <Ionicons name="log-in-outline" size={20} color="#fff" style={{ marginRight: 8 }} />
        <Text style={S.primaryBtnText}>Sign In Now</Text>
      </TouchableOpacity>
    </>
  );

  // ── Step indicator ────────────────────────────────────────────────────────────
  const renderStepBar = () => (
    <View style={S.stepBar}>
      <StepDot step={1} active={step === 0} done={step > 0} />
      <StepLine done={step > 0} />
      <StepDot step={2} active={step === 1} done={step > 1} />
      <StepLine done={step > 1} />
      <StepDot step={3} active={step === 2} done={step > 2} />
      <StepLine done={step > 2} />
      <StepDot step={4} active={step === 3} done={false} />
      <View style={{ marginLeft: 8 }}>
        <Text style={{ fontSize: 11, color: '#90A4AE', fontWeight: '600' }}>
          {['Email', 'Sent', 'Reset', 'Done'][step]}
        </Text>
      </View>
    </View>
  );

  // ── Help section ──────────────────────────────────────────────────────────────
  const renderHelp = () => (
    <View style={S.helpCard}>
      <Text style={S.helpTitle}>Need help?</Text>
      <TouchableOpacity style={S.helpRow}
        onPress={() => { /* Linking.openURL('tel:+442012345678') */ }}>
        <View style={[S.helpIcon, { backgroundColor: '#E3F2FD' }]}>
          <MaterialCommunityIcons name="phone-outline" size={16} color="#1976D2" />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={S.helpLabel}>Phone Support</Text>
          <Text style={S.helpValue}>+44 20 1234 5678</Text>
        </View>
        <Ionicons name="chevron-forward" size={16} color="#B0BEC5" />
      </TouchableOpacity>

      <View style={{ height: 1, backgroundColor: '#F5F5F5', marginVertical: 4 }} />

      <TouchableOpacity style={S.helpRow}
        onPress={() => { /* Linking.openURL('mailto:support@jobeng.ai') */ }}>
        <View style={[S.helpIcon, { backgroundColor: '#E8F5E9' }]}>
          <MaterialCommunityIcons name="email-outline" size={16} color="#388E3C" />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={S.helpLabel}>Email Support</Text>
          <Text style={S.helpValue}>support@jobeng.ai</Text>
        </View>
        <Ionicons name="chevron-forward" size={16} color="#B0BEC5" />
      </TouchableOpacity>

      <View style={{ height: 1, backgroundColor: '#F5F5F5', marginVertical: 4 }} />

      <TouchableOpacity style={S.helpRow}>
        <View style={[S.helpIcon, { backgroundColor: '#FFF3E0' }]}>
          <MaterialCommunityIcons name="help-circle-outline" size={16} color="#F57C00" />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={S.helpLabel}>Help Centre</Text>
          <Text style={S.helpValue}>help.jobeng.ai</Text>
        </View>
        <Ionicons name="chevron-forward" size={16} color="#B0BEC5" />
      </TouchableOpacity>
    </View>
  );

  // ── Main render ──────────────────────────────────────────────────────────────
  const STEP_LABELS = ['Email Address', 'Link Sent', 'New Password', 'All Done'];

  return (
    <SafeAreaView style={S.root}>
      {/* Header */}
      <LinearGradient colors={['#0e4e96', '#1565C0']} style={S.header}>
        <TouchableOpacity onPress={() => step > 0 ? transition(() => setStep(s => s - 1)) : navigation.goBack()}
          style={S.backBtn}>
          <Ionicons name="arrow-back" size={22} color="#fff" />
        </TouchableOpacity>
        <View style={{ flex: 1, alignItems: 'center' }}>
          <Text style={S.headerTitle}>Reset Password</Text>
          <Text style={S.headerSub}>{STEP_LABELS[step]}</Text>
        </View>
        <View style={{ width: 36 }} />
      </LinearGradient>

      {/* Progress bar */}
      <View style={{ height: 3, backgroundColor: '#E0E0E0' }}>
        <Animated.View style={{ height: '100%', width: `${((step + 1) / 4) * 100}%`,
          backgroundColor: step === 3 ? '#388E3C' : '#0e4e96',
          borderTopRightRadius: 2, borderBottomRightRadius: 2 }} />
      </View>

      <KeyboardAvoidingView style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <ScrollView contentContainerStyle={S.scroll} showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled">

          {/* Step indicator */}
          {step < 3 && renderStepBar()}

          {/* Animated step content */}
          <Animated.View style={{ opacity: fade }}>
            {step === 0 && renderStep0()}
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
          </Animated.View>

          {/* Help — always visible except done */}
          {step < 3 && renderHelp()}

          {/* Back to login */}
          {step < 3 && (
            <TouchableOpacity style={S.backLogin} onPress={goToLogin}>
              <Ionicons name="arrow-back" size={15} color="#78909C" />
              <Text style={S.backLoginText}>Back to Login</Text>
            </TouchableOpacity>
          )}
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
const S = StyleSheet.create({
  root:     { flex: 1, backgroundColor: '#F5F7FA' },

  header:   { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16,
    paddingVertical: 14, gap: 10 },
  backBtn:  { width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center', justifyContent: 'center' },
  headerTitle: { fontSize: 17, fontWeight: '800', color: '#fff' },
  headerSub:   { fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 1 },

  scroll:   { padding: 22, paddingBottom: 40 },

  stepBar:  { flexDirection: 'row', alignItems: 'center', marginBottom: 26,
    backgroundColor: '#fff', borderRadius: 14, padding: 14 },
  stepDot:  { width: 26, height: 26, borderRadius: 13, borderWidth: 2,
    borderColor: '#CFD8DC', backgroundColor: '#F5F5F5',
    alignItems: 'center', justifyContent: 'center' },

  iconWrap:   { alignItems: 'center', marginBottom: 22 },
  iconCircle: { width: 104, height: 104, borderRadius: 52,
    alignItems: 'center', justifyContent: 'center' },

  heading:  { fontSize: 26, fontWeight: '900', color: '#263238', textAlign: 'center', marginBottom: 10 },
  sub:      { fontSize: 15, color: '#607D8B', textAlign: 'center', lineHeight: 22, marginBottom: 28 },

  label:    { fontSize: 13, fontWeight: '700', color: '#546E7A' },
  inputBox: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff',
    borderRadius: 12, paddingHorizontal: 14, paddingVertical: 14,
    borderWidth: 1.5, borderColor: '#E0E0E0' },
  input:    { flex: 1, fontSize: 15, color: '#263238' },
  errText:  { fontSize: 12, color: '#D32F2F', fontWeight: '600' },

  primaryBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#0e4e96', borderRadius: 14, paddingVertical: 16,
    marginBottom: 12, shadowColor: '#0e4e96', shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3, shadowRadius: 8, elevation: 5 },
  primaryBtnDisabled: { backgroundColor: '#B0BEC5', shadowOpacity: 0 },
  primaryBtnText: { fontSize: 16, fontWeight: '800', color: '#fff' },

  outlineBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    borderWidth: 2, borderColor: '#0e4e96', borderRadius: 14, paddingVertical: 14 },
  outlineBtnText: { fontSize: 15, fontWeight: '700', color: '#0e4e96' },

  securityBox: { flexDirection: 'row', alignItems: 'flex-start', gap: 10,
    backgroundColor: '#E8F5E9', borderRadius: 12, padding: 14, marginTop: 4,
    borderWidth: 1, borderColor: '#C8E6C9', marginBottom: 20 },
  securityText: { flex: 1, fontSize: 13, color: '#2E7D32', lineHeight: 19 },

  emailPill: { flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#E3F2FD', borderRadius: 20, paddingHorizontal: 16, paddingVertical: 10,
    alignSelf: 'center', marginBottom: 22, maxWidth: width - 80,
    borderWidth: 1, borderColor: '#BBDEFB' },
  emailPillText: { fontSize: 14, fontWeight: '700', color: '#0e4e96' },

  stepsGuide: { backgroundColor: '#fff', borderRadius: 14, padding: 16, marginBottom: 16 },
  stepGuideRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8,
    borderBottomWidth: 1, borderBottomColor: '#F5F5F5' },
  stepGuideNum: { width: 22, height: 22, borderRadius: 11, backgroundColor: '#E3F2FD',
    alignItems: 'center', justifyContent: 'center' },

  spamNote: { flexDirection: 'row', alignItems: 'flex-start', gap: 8,
    backgroundColor: '#F5F5F5', borderRadius: 10, padding: 12, marginBottom: 10 },
  spamText: { flex: 1, fontSize: 12, color: '#78909C', lineHeight: 17 },

  strengthTag: { flexDirection: 'row', alignItems: 'center', borderRadius: 12, borderWidth: 1,
    paddingHorizontal: 8, paddingVertical: 3 },

  successList: { backgroundColor: '#fff', borderRadius: 14, padding: 16, marginBottom: 24 },

  helpCard:  { backgroundColor: '#fff', borderRadius: 16, padding: 16, marginTop: 10, marginBottom: 10,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06, shadowRadius: 5, elevation: 2 },
  helpTitle: { fontSize: 14, fontWeight: '800', color: '#263238', marginBottom: 12 },
  helpRow:   { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, gap: 12 },
  helpIcon:  { width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center' },
  helpLabel: { fontSize: 12, color: '#78909C', fontWeight: '600' },
  helpValue: { fontSize: 14, color: '#263238', fontWeight: '600', marginTop: 1 },

  backLogin: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 6, paddingVertical: 16 },
  backLoginText: { fontSize: 14, color: '#78909C' },
});